// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import clsx from 'clsx';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { ActionButton } from 'office-ui-fabric-react';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Address from './Address';
import history from 'src/history';
import {
    deviceInformationSelector,
    deviceSelector
} from './../../store/deviceInfo/selectors';
import {
    getSelectedShippingOfferData,
    Progress,
    progressSelector,
    selectedShippingOfferSelector,
    shippingAddressSelector,
    shippingOfferSelector,
    zipCodeSelector,
    allSelectedOfferSelector,
    reActivateOrderStatusSelector,
    isOnSiteRepairStatusSelector,
    z4rReturnReasonsSelector,
    updateCreateOrderProgress,
    isSoftwareOrderSelector,
} from './../../store/orderCreation';

import DeviceInformation from './../../components/DeviceInformation';
import CommonLayout from './../../components/CommonLayout';
import ErrorMessage from './../../components/ErrorMessage';
import Registration from './../../components/Registration';
import ShippingMethodOffers from './../../components/ShippingMethod';
import ZipCode from './ZipCode';

import RepairOffer from './RepairOffer/RepairOffer';
import ReplacementNewSerialNumber from './ReplacementNewSerialNumber/ReplacementNewSerialNumber';
import SelectRepairItems from './SelectRepairItems/SelectRepairItems';

import Styles from './styles.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';
import {
    checkOffersForShippingOffer,
    defaultToDatabaseShipToAddress,
    defaultToWalkCenterAddressSelected,
    isShippingAddressRequired,
    walkCenterIsMixedWithOther,
} from 'src/helpers/shippingChecks';
import { selectedCountryInfoSelector } from './../../store/countries/selectors';
import { wicSelectedAddressSelector } from './../../store/address';
import BillingAddress from './BillingAddress/BillingAddress';
import { isBillingAddressRequired } from './../../helpers/billingCheck';
import { userAgentTierSelector } from './../../store/user';
import { checkIsUserEnterpriseCategoryFromTier } from './../../lib/userTierConfig';

export default function CreateOrder() {
    const { t } = useTranslation();

    const [bypassSerialReplacement, setBypassShowSerialReplacement] = React.useState(false);
    const [bypassShipping, setBypassShipping] = React.useState(false);
    const [doNotContinue, setDoNotContinue] = React.useState(false);
    const device = useSelector(state => deviceSelector(state));
    const zipCode = useSelector(state => zipCodeSelector(state));
    const currentStage: Progress = useSelector(state => progressSelector(state));
    const countryInfo = useSelector(state => selectedCountryInfoSelector(state));
    const country = countryInfo && countryInfo.countryName;
    const reActivateOrder = useSelector(state => reActivateOrderStatusSelector(state));

    if (!device.serialNumber) {
        console.log(t('serial_number_is_missing_error'));
        history.replace('/');
        return null;
    }

    const deviceInformation = useSelector(state =>
        deviceInformationSelector(state)
    );

    const deviceProps = {
        zipCode,
        country,
        isCreateOrderView: true,
        description: deviceInformation.description,
        deviceName: deviceInformation.repairProduct.name,
        image: deviceInformation.repairProduct.imageUri,
        marketPlace: deviceInformation.marketplaceName,
        partNumber: deviceInformation.partNumber,
        resetPath: '/',
        serialNumber: device.serialNumber,
        seriesName: deviceInformation.msPressSeriesName,
        status: device.status,
        deviceSDTRunDetail: device.deviceSDTRunDetail,
        countryOrigin: device.attributes?.countryOfOrigin,
        color: device.attributes?.color,
    };

    const progress: Progress = (!zipCode) ? Progress.ZipCode : currentStage;

    const showSections = {
        zipCode: Progress.ZipCode === progress,

        selectRepairItems: Progress.ItemSelection === progress,

        repairOffers:
            Progress.RepairOfferSelection === progress ||
            Progress.AddingShippingAddress === progress ||
            Progress.UpdateDeviceNewSerialNumber === progress ||
            Progress.AddingBillingAddress === progress ||
            Progress.Registration === progress,

        replacementSn:
            Progress.UpdateDeviceNewSerialNumber === progress ||
            Progress.AddingShippingAddress === progress ||
            Progress.AddingBillingAddress === progress ||
            Progress.Registration === progress,

        shipping:
            Progress.AddingShippingAddress === progress ||
            Progress.AddingBillingAddress === progress ||
            Progress.Registration === progress,

        billing:
            Progress.AddingBillingAddress === progress ||
            Progress.Registration === progress,

        registration: Progress.Registration === progress,
    };

    function setRefs(autoFocus: boolean) {
        return (el: HTMLDivElement) => {
            if (autoFocus && el) el.focus();
        };
    }

    React.useEffect(() => {
        if (doNotContinue && !showSections.shipping) {
            setDoNotContinue(false);
        }

        if (reActivateOrder && showSections.registration) {
            setDoNotContinue(false);
        }
    }, [progress]);

    return (
        <>
            {
                // Have to redirect inside render else React complains about "rendered fewer hooks than expected"
                // Inspiration: https://dev.to/httpjunkie/programmatically-redirect-in-react-with-react-router-and-hooks-3hej
                progress === Progress.PlacingOrder
                    ? (
                        <Redirect to={{ pathname: '/billingAndReview', state: { deviceProps } }} />
                    ) : null
            }
            <CommonLayout
                pageTitle={UpdatePageTitle(progress)}
                skipLinks={GetSkipLinkMenu(showSections, bypassSerialReplacement, bypassShipping)}
            >
                <main className={Styles.bodyContainer}>
                    <section className={Styles.sectionContainer}>
                        <DeviceInformation {...deviceProps} />
                    </section>
                    <section className={`${Styles.sectionContainer} ${Styles.sectionSeparator}`}>
                        {showSections.zipCode && <ZipCode />}

                        {showSections.selectRepairItems && <SelectRepairItems />}

                        {showSections.repairOffers && <RepairOffer
                            readonly={!(Progress.RepairOfferSelection === progress)}
                            ref={setRefs(progress === Progress.RepairOfferSelection)}
                        />}

                        {showSections.replacementSn && <ReplacementNewSerialNumber
                            setBypassSerialReplacement={setBypassShowSerialReplacement}
                        />}

                        {showSections.shipping && <RenderShippingAddress
                            readonly={!(Progress.AddingShippingAddress === progress)}
                            setBypassShipping={setBypassShipping}
                            setDoNotContinue={setDoNotContinue}
                        />}

                        {(showSections.billing && !doNotContinue) &&
                            <BillingAddress
                                readonly={!(Progress.AddingBillingAddress === progress)}
                            />
                        }

                        {(showSections.registration && !doNotContinue) && <Registration />}

                    </section>
                </main>
            </CommonLayout>
        </>
    );
}

 function RenderShippingAddress(props: any) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [isEditing, setIsEditing] = React.useState(false);
    const [isOrderSummaryEditing, setOrderSummaryEditing] = React.useState(false);
    const [customerComments, setCustomerComments] = React.useState<string | undefined>('');

    const shippingOffers = useSelector(state => shippingOfferSelector(state));
    const selectedShippingOffers = useSelector(state => selectedShippingOfferSelector(state));
    const selectedOffers = useSelector(state => allSelectedOfferSelector(state));
    const z4rReturnReasons = useSelector(state => z4rReturnReasonsSelector(state));
    const wicSelectedAddress = useSelector(state => wicSelectedAddressSelector(state));
     const userAgentTier = useSelector(state => userAgentTierSelector(state));

    const shippingAddressRequired = isShippingAddressRequired(shippingOffers, selectedOffers);
    const isWalkInCenterMixedWithOtherOffers = walkCenterIsMixedWithOther(selectedShippingOffers);
    const isOnSiteRepair = useSelector(state => isOnSiteRepairStatusSelector(state));
    const isSoftwareOrder = useSelector(state => isSoftwareOrderSelector(state));

    //to get the current tier information and check whether Enterprise category or not
    const isEnterpriseCustomer = checkIsUserEnterpriseCategoryFromTier(userAgentTier);

    // if there is a single offer without shipping offer
    const doesOfferExistWithoutShippingOffer = shippingOffers.some(shippingOffer => !Array.isArray(shippingOffer.offers) || shippingOffer.offers.length === 0);
    const reActivateOrder = useSelector(state => reActivateOrderStatusSelector(state));
    if (doesOfferExistWithoutShippingOffer) {
        props.setDoNotContinue(true);
        const offerWithoutShippingOffer = shippingOffers.filter(shippingOffer => !Array.isArray(shippingOffer.offers) || shippingOffer.offers.length === 0);

        const message = offerWithoutShippingOffer.map(offer =>
            (<ErrorMessage className={Styles.shippingMethod} message={t('shipping_offer_not_configured', { componentName: offer.title })} traceData={{ shippingOffers }} />));

        return (<>{message}</>);
    }
    if (isWalkInCenterMixedWithOtherOffers) {
        props.setDoNotContinue(true);
        return (<ErrorMessage className={Styles.shippingMethod} message={t('walk_in_center_cannot_be_mixed')} traceData={{ shippingOffers }} />);
    }

    if (!shippingAddressRequired) {
        props.setBypassShipping(true);
        return null;
    }

    const items = shippingOffers.map((shippingOffer: any) => {
        const matchedZ4RReason = (z4rReturnReasons || []).find(
            (reason: any) => reason?.accessoryType === shippingOffer.symptomCode
        ) || {};

        return {
            title: shippingOffer.title,
            offerSku: shippingOffer.offerSku,
            symptomCode: shippingOffer.symptomCode,
            reasonForZ4R: matchedZ4RReason.reason || '',
            reasonForZ4RDescription: matchedZ4RReason.repairComments || '',
            onSelectionChanged: onShippingOfferSelected,
            items: shippingOffer.offers.map((offer: any) => ({
                key: offer.offerSku, text: offer.name, ...offer,
            })),
            onCustomerCommentUpdate: customerCommentUpdate,
        };
    }).filter((offer: any) => (checkOffersForShippingOffer([{ offers: offer.items }])));

    function onShippingOfferSelected(selectedItem: any) {
        dispatch(getSelectedShippingOfferData({ [selectedItem.itemOfferSku]: selectedItem }));
    }

    function handleEdit() {
        logPageAction('Shipping method edit button clicked');
        setIsEditing(true);
        setOrderSummaryEditing(true);
        dispatch(updateCreateOrderProgress(Progress.AddingShippingAddress));
    }

    function customerCommentUpdate(value: string | undefined) {
        setCustomerComments(value);
    }

    //user must select shipping offer for all device|part /accessory
    const enableContinue = !isWalkInCenterMixedWithOtherOffers && selectedShippingOffers
        && shippingOffers.filter((shippingOffer: any) => checkOffersForShippingOffer([{ offers: shippingOffer.offers }]))
            .every((offer: any) =>
                selectedShippingOffers[offer.offerSku]);

    const EditButton = () =>
        <span className={Styles.editContainer}>
            <Icon className={clsx('ms-Icon', Styles.editIcon)} iconName="edit" ariaLabel={t('edit')} />
            <ActionButton aria-label={t('edit_shipping')} onClick={handleEdit}>{t('edit')}</ActionButton>
        </span>;

    const shippingAddress = useSelector(state => shippingAddressSelector(state));

    function onContinueClick() {
        setIsEditing(false);
    }

    return (
        <section id="shipping">
            <div className="ms-Grid" dir="ltr">
                <div className={clsx(Styles.headerContainer, 'ms-Grid-row')}>
                    <div className={clsx('ms-Grid-col', 'ms-sm12')}>
                        <h2 className={clsx(Styles.header, Styles.h2Heading)}>{isOnSiteRepair ?
                            t('customer_service_location_address') : isSoftwareOrder ?
                                t('software_order_details') : t('shipping')}</h2>
                        {shippingAddress && !isEditing && !reActivateOrder && <EditButton />}
                    </div>
                </div>
            </div>
            <ShippingMethodOffers
                offers={items}
                readonly={reActivateOrder && !isEditing ? false : props.readonly && !isEditing}
                isOrderSummaryEditing={isOrderSummaryEditing}
                onContinueClick={onContinueClick}
                ishidden={isSoftwareOrder}
            />
            {!reActivateOrder &&
                <Address
                    isBillingRequired={isBillingAddressRequired(selectedShippingOffers, selectedOffers, userAgentTier)}
                    onContinueClick={onContinueClick}
                    defaultToWicShipToAddress={defaultToWalkCenterAddressSelected(shippingOffers, wicSelectedAddress)}
                    defaultToShipToAddress={defaultToDatabaseShipToAddress(shippingOffers, selectedOffers)}
                    enableContinue={enableContinue}
                    readonly={reActivateOrder ? false : props.readonly && !isEditing}
                    isEnterpriseCustomer={isEnterpriseCustomer}
                    isOrderSummaryEditing={isOrderSummaryEditing}
                    customerComments={customerComments} />
            }
        </section>
    );
}

// A11Y - accessibility menu for skipping to main sections
function GetSkipLinkMenu(showSections: any, bypassSerialReplacement: boolean, bypassShipping: boolean) {
    const { t } = useTranslation();

    const menu = [];
    if (showSections.zipCode) {
        menu.push({
            key: 'zip_code',
            text: t('zip_code'),
        });
    }
    if (showSections.selectRepairItems || showSections.repairOffers) {
        menu.push({
            key: 'select_repair_items',
            text: t('select_repair_items'),
        });
    }
    if (showSections.replacementSn && !bypassSerialReplacement) {
        menu.push({
            key: 'replacement_sn',
            text: t('replacement_serial_number'),
        });
    }
    if (showSections.shipping && !bypassShipping) {
        menu.push({
            key: 'shipping',
            text: t('shipping'),
        });
    }
    if (showSections.registration) {
        menu.push({
            key: 'registration',
            text: t('registration'),
        });
    }

    return menu;
}

function UpdatePageTitle(progress: Progress): string[] {
    const { t } = useTranslation();

    const titles = [t('create_order')];

    switch (progress) {
        case Progress.AddingBillingAddress:
            titles.push(t('billing_address'));
            break;
        case Progress.AddingShippingAddress:
            titles.push(t('shipping_address'));
            break;
        case Progress.ItemSelection:
            titles.push(t('repair_items'));
            break;
        case Progress.Registration:
            titles.push(t('registration'));
            break;
        case Progress.RepairOfferSelection:
            titles.push(t('repair_offers'));
            break;
        case Progress.UpdateDeviceNewSerialNumber:
            titles.push(t('new_serial_number'));
            break;
        case Progress.ZipCode:
            titles.push(t('zip_code'));
            break;
    }

    return titles;
}